// client/src/components/checkin/CheckInHistory.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import api from '../../services/api'; // Use the default export

const CheckInHistory = ({ token }) => {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      if (!token) {
        setError('Authentication token is missing.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await api.getCheckIns(token);
        // Sort by date descending just in case API doesn't guarantee it
        data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setCheckIns(data);
      } catch (err) {
        console.error("Error fetching check-in history:", err);
        setError(err.message || 'Failed to load check-in history.');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [token]); // Re-fetch if token changes

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric'
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 1 }}> {/* Make list scrollable */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2, textAlign: 'center' }}>
        Your Check-In History
      </Typography>
      {checkIns.length === 0 ? (
        <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
          No check-ins recorded yet.
        </Typography>
      ) : (
        <List component={Paper} elevation={2} sx={{ bgcolor: 'background.paper' }}>
          {checkIns.map((checkIn, index) => (
            <React.Fragment key={checkIn._id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="div">
                      {formatDate(checkIn.date)}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        sx={{ display: 'block' }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        Mood: {checkIn.moodRating}/5 | Anxiety: {checkIn.anxietyRating}/5
                      </Typography>
                      {checkIn.notes && (
                        <Typography
                          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          Notes: {checkIn.notes}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
              {index < checkIns.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default CheckInHistory;
